/**
 * =============================================================================
 * STATUS BADGE COMPONENT
 * =============================================================================
 *
 * Color-coded badge for displaying question status.
 * Uses BigDesign Badge component for consistent styling.
 *
 * ARCHITECTURE REFERENCE: Section 10 - Admin Dashboard
 * PHASE_0B_SPEC.md: Task 2.4.3
 *
 * =============================================================================
 */

import React from 'react';
import { StatusChip } from '../common/StatusChip';
import { QUESTION_STATUS_LABELS, type QuestionStatus } from '@find-my-part/types';

// Re-export for convenience
export type { QuestionStatus } from '@find-my-part/types';

/**
 * Props for StatusBadge component.
 */
export interface StatusBadgeProps {
  /** The question status to display */
  status: QuestionStatus | string;
  /** Optional test ID for testing */
  'data-testid'?: string;
}

/**
 * Status configuration for badge display.
 */
interface StatusConfig {
  label: string;
  variant: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
}

/**
 * Maps status values to display configuration.
 * Uses QUESTION_STATUS_LABELS from @find-my-part/types for consistency.
 *
 * PENDING_REVIEW: yellow (warning) - needs attention
 * OPEN: green (success) - approved and active
 * CLOSED_REJECTED: red (danger) - rejected
 * Other statuses use secondary styling in Phase 0B.
 */
const STATUS_CONFIG: Record<QuestionStatus, StatusConfig> = {
  PENDING_REVIEW: {
    label: QUESTION_STATUS_LABELS.PENDING_REVIEW,
    variant: 'warning',
  },
  OPEN: {
    label: QUESTION_STATUS_LABELS.OPEN,
    variant: 'success',
  },
  IN_PROGRESS: {
    label: QUESTION_STATUS_LABELS.IN_PROGRESS,
    variant: 'primary',
  },
  NEEDS_MORE_INFO: {
    label: QUESTION_STATUS_LABELS.NEEDS_MORE_INFO,
    variant: 'warning',
  },
  RESOLVED: {
    label: QUESTION_STATUS_LABELS.RESOLVED,
    variant: 'success',
  },
  CLOSED_DUPLICATE: {
    label: QUESTION_STATUS_LABELS.CLOSED_DUPLICATE,
    variant: 'secondary',
  },
  CLOSED_REJECTED: {
    label: QUESTION_STATUS_LABELS.CLOSED_REJECTED,
    variant: 'danger',
  },
};

/**
 * Fallback configuration for unknown status values.
 */
const UNKNOWN_STATUS_CONFIG: StatusConfig = {
  label: 'Unknown',
  variant: 'secondary',
};

/**
 * StatusBadge component.
 *
 * Displays a color-coded badge indicating the question's current status.
 * Colors are designed to provide quick visual feedback:
 * - Yellow (warning): Requires attention (PENDING_REVIEW, NEEDS_MORE_INFO)
 * - Green (success): Active/resolved (OPEN, RESOLVED)
 * - Red (danger): Rejected (CLOSED_REJECTED)
 * - Blue (primary): In progress (IN_PROGRESS)
 * - Gray (secondary): Other states (CLOSED_DUPLICATE)
 *
 * @example
 * ```tsx
 * <StatusBadge status="PENDING_REVIEW" />
 * <StatusBadge status="OPEN" />
 * <StatusBadge status="CLOSED_REJECTED" />
 * ```
 */
export function StatusBadge({
  status,
  'data-testid': testId,
}: StatusBadgeProps): React.ReactElement {
  const config = STATUS_CONFIG[status as QuestionStatus] ?? UNKNOWN_STATUS_CONFIG;

  return <StatusChip variant={config.variant} label={config.label} testId={testId} />;
}
